import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";

// Telluria
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// Hooks
import { useAuth } from "@hooks/useAuth";

/** Atoms */
import { Message } from "@atoms/index";

// Libraries
import { Send } from "@libraries/icons";

// graphql
import {
  ConcretingRequestMessageType,
  EWhereClausesOperators,
  ConcretingRequestMessageQueriesGetAllWithPaginationArgs,
  ECommandResultStatus,
  ConcretingRequestMessageMutationsCreateArgs
} from "@store/graphql/helpers/types.graphql";
import {
  concretingRequestMessageAllWithPagination,
  TConcretingRequestMessageAllWithPaginationQueries
} from "@store/graphql/groups/concretingRequestMessage/query.graphql";
import {
  concretingRequestMessageCreate,
  TConcretingRequestMessageCreateMutations
} from "@store/graphql/groups/concretingRequestMessage/mutation.graphql";

// Language
import { ConcretingApprovalMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";

import * as Styled from "./styles";

interface MessagesBoxProps {
  concretingRequestId: string;
}

const MessagesBox: React.FC<MessagesBoxProps> = ({ concretingRequestId }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const messageContentRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ConcretingRequestMessageType[]>([]);
  const [messageInput, setMessageInput] = useState("");

  const [MutationCreateConcretingRequestMessage] = useMutation<
    TConcretingRequestMessageCreateMutations,
    ConcretingRequestMessageMutationsCreateArgs
  >(concretingRequestMessageCreate(["id"]));

  const {
    loading: loadingConcretingRequestMessages,
    error: errorConcretingRequestMessages,
    data: dataConcretingRequestMessages,
    refetch: refetchConcretingRequestMessages
  } = useQuery<
    TConcretingRequestMessageAllWithPaginationQueries,
    ConcretingRequestMessageQueriesGetAllWithPaginationArgs
  >(
    concretingRequestMessageAllWithPagination([
      "id",
      "message",
      "createdAt",
      "user.id",
      "user.fullName",
      "user.role"
    ]),
    {
      fetchPolicy: "network-only",
      variables: {
        where: [
          {
            property: "concretingRequest.id",
            operator: EWhereClausesOperators.EQUALS,
            value: concretingRequestId
          }
        ]
      }
    }
  );

  const loadMessages = useCallback(async () => {
    const response = dataConcretingRequestMessages?.concretingRequestMessage
      .getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const concretingRequestsSorted = response.result?.sort((a, b) => (a?.createdAt > b?.createdAt ? 1 : -1));

      setMessages(concretingRequestsSorted as ConcretingRequestMessageType[]);
    }
  }, [dataConcretingRequestMessages]);

  const scrollMessageContent = useCallback(() => {
    if (messageContentRef.current) {
      messageContentRef.current.scrollTop = messageContentRef.current.scrollHeight;
    }
  }, [messageContentRef]);

  const createMessage = useCallback(async () => {
    const mutation = await MutationCreateConcretingRequestMessage({
      variables: {
        concretingRequestMessage: {
          message: messageInput,
          concretingRequestId,
          userId: token.primarysid
        }
      }
    });

    return mutation.data?.concretingRequestMessage.create;
  }, [
    MutationCreateConcretingRequestMessage,
    concretingRequestId,
    messageInput,
    token
  ]);

  const handleCreateMessage = useCallback(async () => {
    try {
      const response = await createMessage();

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      setMessageInput("");
      scrollMessageContent();
      refetchConcretingRequestMessages();
    } catch (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    }
  }, [
    createMessage,
    addToast,
    refetchConcretingRequestMessages,
    scrollMessageContent
  ]);

  useEffect(() => {
    if (dataConcretingRequestMessages) loadMessages();
    if (errorConcretingRequestMessages) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorConcretingRequestMessages.message
      });
    }
  }, [
    addToast,
    dataConcretingRequestMessages,
    errorConcretingRequestMessages,
    loadMessages
  ]);

  useEffect(() => {
    if (loadingConcretingRequestMessages || !messageContentRef.current) return;

    scrollMessageContent();
  }, [loadingConcretingRequestMessages, scrollMessageContent]);

  return (
    <Styled.Container>
      <Styled.MessagesContent ref={messageContentRef}>
        {messages.map((message) => {
          if (!message) return null;

          return <Message key={message.id} message={message} />;
        })}
      </Styled.MessagesContent>

      <Styled.InputContainer>
        <Styled.InputContent>
          <Styled.Input
            type="text"
            multiline
            size="small"
            placeholder={t(ConcretingApprovalMessages.messageInputPlaceholder)}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />

          <Styled.SendButton
            onClick={handleCreateMessage}
            disabled={messageInput.length === 0}
          >
            <Send />
          </Styled.SendButton>
        </Styled.InputContent>
      </Styled.InputContainer>
    </Styled.Container>
  );
};

export default MessagesBox;
